import { YearAndPeriod } from "@/models/";
import PercentageChangeText from "../molecules/percentageChangeText";
import { Bar, BarChart, LabelList, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import airbnbLeafLeft from "@/staticfiles/airbnb-leaf-left.png";
import airbnbLeafRight from "@/staticfiles/airbnb-leaf-right.png";
import { Apartment, ApartmentStats, OwnerAirbnbReview } from "@/models/responses";
import { reportBarChartNumberFormatter, reportGraphsAdrFormatter, reportGraphsIncomeFormatter, reportGraphsOccupancyFormatter } from "@/utils/formatters";
import CustomLabel from "../molecules/barChartCustomLabel";
import React from "react";
import AirbnbLogo from "@/lib/molecules/airbnb-logo";
import { findPeriodStatsForApartment } from "./reportGraphsView.utils";
import apartmentPlaceholder from "@/staticfiles/apartment-placeholder.svg";

interface ReportGraphsViewProps {
    yearAndPeriod: YearAndPeriod;
    apartments: Apartment[];
    apartmentStats: ApartmentStats[];
    airbnbReviews: OwnerAirbnbReview[];
    periodBreakdownStats: ApartmentStats[][];
    periodNames: string[];
    previousStats: ApartmentStats[];
}

interface ApartmentBarChartStats {
    name: string;
    value: number;
}

const defaultApartmentImage = apartmentPlaceholder;

function barChart(data: ApartmentBarChartStats[]) {
    const values = data.map((d) => d.value);
    const rawMin = Math.min(0, ...values);
    const rawMax = Math.max(0, ...values);
    // Pad both ends so the zero line always sits inside the plot with headroom.
    // Without this, recharts pins the category axis to the data edge, so an
    // all-negative series fills upward from the bottom and reads as if positive.
    const pad = (rawMax - rawMin || 1) * 0.15;

    return (
        <ResponsiveContainer width="100%" height={105}>
            <BarChart data={data} margin={{ top: 0, right: 0, bottom: -15, left: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
                <YAxis hide domain={[rawMin - pad, rawMax + pad]} />
                {/* The zero line is the visual baseline: positive bars rise above it,
                    negative bars hang below it. */}
                <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1} />
                <Tooltip />
                <Bar dataKey="value" fill="#1F2937" barSize={20}>
                    <LabelList content={<CustomLabel barValueFormatter={reportBarChartNumberFormatter} fontSize={8} />} position="top" />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

function getPeriodBreakdownStats(periodStats: ApartmentStats[], periodNames: string[], attribute: keyof ApartmentStats) {
    if (!periodStats || !periodNames) {
        return [];
    }
    let periodBreakdownStats: ApartmentBarChartStats[] = [];
    for (let i = 0; i < periodNames.length; i++) {
        periodBreakdownStats.push({
            name: periodNames[i],
            value: (periodStats.length > i ? periodStats[i][attribute] : 0) || 0,
        });
    }
    return periodBreakdownStats;
}

const ReportGraphsView: React.FC<ReportGraphsViewProps> = ({ apartments, apartmentStats, airbnbReviews, periodBreakdownStats, previousStats, periodNames }) => {
    return (
        <div className="space-y-6">
            {apartments.map((apartment: Apartment, index: number) => {
                let currentYearStats = apartmentStats.find((stat) => stat.apartmentId === apartment.id);
                let previousYearStats = previousStats.find((stat) => stat.apartmentId === apartment.id);
                let apartmentReview = airbnbReviews.find((review) => review.apartmentId === apartment.id);
                let daysBookedChange = 0,
                    occupancyChange = 0,
                    netIncomeChange = 0,
                    netAdrChange = 0,
                    grossAdrChange = 0;
                let periodStats = findPeriodStatsForApartment(periodBreakdownStats, apartment.id);

                if (currentYearStats && previousYearStats) {
                    daysBookedChange = Math.round(((currentYearStats.daysBooked - previousYearStats.daysBooked) / previousYearStats.daysBooked) * 100);
                    occupancyChange = Math.round(((currentYearStats.occupancy - previousYearStats.occupancy) / previousYearStats.occupancy) * 100);
                    netIncomeChange = Math.round(((currentYearStats.netIncome - previousYearStats.netIncome) / previousYearStats.netIncome) * 100);
                    netAdrChange = Math.round(((currentYearStats.netAdr - previousYearStats.netAdr) / previousYearStats.netAdr) * 100);
                    grossAdrChange = Math.round(((currentYearStats.grossAdr - previousYearStats.grossAdr) / previousYearStats.grossAdr) * 100);
                }
                return (
                    <React.Fragment key={apartment.id}>
                        <div className="flex flex-col space-y-2">
                            {/* Grid layout: mobile 2col, tablet 3col (img+2 charts, then 3), desktop 6col (all in row) */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-2 lg:gap-1 items-end report-apartment-row print:flex print:flex-row print:gap-1">
                                {/* Image column */}
                                <div className="col-span-2 md:col-span-1 print:w-[120px] print:min-w-[120px] print:flex-shrink-0 report-apartment-image-col">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs italic">{apartment.shortName}</span>
                                        {currentYearStats && (
                                            <span className="text-xs font-semibold">
                                                Days Available:{" "}
                                                <span className="text-green-600">{currentYearStats.availableBookingDays - currentYearStats.daysBlocked}</span>
                                            </span>
                                        )}
                                    </div>
                                    <img
                                        className="w-full max-w-[250px] md:max-w-none h-auto rounded-md print:w-[120px] print:h-auto"
                                        src={apartment.imageUrl || defaultApartmentImage}
                                        alt={apartment.name}
                                        onError={(e) => {
                                            // Fall back to the local placeholder; clear the handler so a
                                            // failing placeholder can't loop.
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = defaultApartmentImage;
                                        }}
                                    />
                                </div>

                                {/* Days Booked */}
                                <div className="flex flex-col report-chart-column">
                                    <div className="flex flex-col lg:flex-row lg:items-baseline">
                                        <span className="text-[10px] text-muted-foreground lg:text-xs lg:text-current">Days Booked: </span>
                                        <span className="text-sm font-bold text-foreground lg:text-xs">{currentYearStats?.daysBooked}</span>
                                    </div>
                                    <div className="flex items-center text-[10px] text-muted-foreground lg:text-xs lg:text-current mt-1">
                                        <span className="mr-1 hidden lg:inline">YoY Change:</span>
                                        <span className="lg:hidden">YoY: </span>
                                        <PercentageChangeText value={daysBookedChange} description={""} />
                                    </div>
                                    <div className="mt-2 w-full lg:w-[90%] h-[105px] report-chart-container">
                                        {barChart(getPeriodBreakdownStats(periodStats, periodNames, "daysBooked"))}
                                    </div>
                                </div>
                                {/* Occupancy */}
                                <div className="flex flex-col report-chart-column">
                                    <div className="flex flex-col lg:flex-row lg:items-baseline">
                                        <span className="text-[10px] text-muted-foreground lg:text-xs lg:text-current">Occupancy: </span>
                                        <span className="text-sm font-bold lg:text-xs lg:font-bold">
                                            {reportGraphsOccupancyFormatter(currentYearStats?.occupancy)}
                                        </span>
                                    </div>
                                    <div className="flex items-center text-[10px] text-muted-foreground lg:text-xs lg:text-current mt-1">
                                        <span className="mr-1 hidden lg:inline">YoY Change:</span>
                                        <span className="lg:hidden">YoY: </span>
                                        <PercentageChangeText value={occupancyChange} description={""} />
                                    </div>
                                    <div className="mt-2 w-full lg:w-[90%] h-[105px] report-chart-container">
                                        {barChart(getPeriodBreakdownStats(periodStats, periodNames, "occupancy"))}
                                    </div>
                                </div>
                                {/* Net Income */}
                                <div className="flex flex-col report-chart-column">
                                    <div className="flex flex-col lg:flex-row lg:items-baseline">
                                        <span className="text-[10px] text-muted-foreground lg:text-xs lg:text-current">Net Income: </span>
                                        <span className="text-sm font-bold lg:text-xs lg:font-bold">
                                            {reportGraphsIncomeFormatter(currentYearStats?.netIncome)}
                                        </span>
                                    </div>
                                    <div className="flex items-center text-[10px] text-muted-foreground lg:text-xs lg:text-current mt-1">
                                        <span className="mr-1 hidden lg:inline">YoY Change:</span>
                                        <span className="lg:hidden">YoY: </span>
                                        <PercentageChangeText value={netIncomeChange} description={""} />
                                    </div>
                                    <div className="mt-2 w-full lg:w-[90%] h-[105px] report-chart-container">
                                        {barChart(getPeriodBreakdownStats(periodStats, periodNames, "netIncome"))}
                                    </div>
                                </div>
                                {/* Gross ADR */}
                                <div className="flex flex-col report-chart-column">
                                    <div className="flex flex-col lg:flex-row lg:items-baseline">
                                        <span className="text-[10px] text-muted-foreground lg:text-xs lg:text-current">Gross ADR: </span>
                                        <span className="text-sm font-bold lg:text-xs lg:font-bold">
                                            {reportGraphsAdrFormatter(currentYearStats?.grossAdr)}
                                        </span>
                                    </div>
                                    <div className="flex items-center text-[10px] text-muted-foreground lg:text-xs lg:text-current mt-1">
                                        <span className="mr-1 hidden lg:inline">YoY Change:</span>
                                        <span className="lg:hidden">YoY: </span>
                                        <PercentageChangeText value={grossAdrChange} description={""} />
                                    </div>
                                    <div className="mt-2 w-full lg:w-[90%] h-[105px] report-chart-container">
                                        {barChart(getPeriodBreakdownStats(periodStats, periodNames, "grossAdr"))}
                                    </div>
                                </div>
                                {/* Net ADR */}
                                <div className="flex flex-col report-chart-column">
                                    <div className="flex flex-col lg:flex-row lg:items-baseline">
                                        <span className="text-[10px] text-muted-foreground lg:text-xs lg:text-current">Net ADR: </span>
                                        <span className="text-sm font-bold lg:text-xs lg:font-bold">{reportGraphsAdrFormatter(currentYearStats?.netAdr)}</span>
                                    </div>
                                    <div className="flex items-center text-[10px] text-muted-foreground lg:text-xs lg:text-current mt-1">
                                        <span className="mr-1 hidden lg:inline">YoY Change:</span>
                                        <span className="lg:hidden">YoY: </span>
                                        <PercentageChangeText value={netAdrChange} description={""} />
                                    </div>
                                    <div className="mt-2 w-full lg:w-[90%] h-[105px] report-chart-container">
                                        {barChart(getPeriodBreakdownStats(periodStats, periodNames, "netAdr"))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-hidden">
                            {apartmentReview && (
                                <>
                                    <h3 className="font-semibold text-foreground mb-6">Review Score</h3>
                                    {apartmentReview.overallScore > 4.8 ? (
                                        <div className="border border-border rounded-xl p-4 sm:p-6 flex flex-col sm:flex-row items-center sm:justify-between gap-4 sm:gap-8">
                                            <AirbnbLogo className="h-8 sm:h-10 text-foreground" />
                                            <div className="flex items-center gap-2 sm:gap-4">
                                                <img src={airbnbLeafLeft} alt="" className="w-6 h-6 sm:w-10 sm:h-10" aria-hidden="true" />
                                                <div className="flex text-center flex-col">
                                                    <span className="text-base sm:text-xl font-bold text-foreground">Guest</span>
                                                    <span className="text-base sm:text-xl font-bold text-foreground">favorite</span>
                                                </div>
                                                <img src={airbnbLeafRight} alt="" className="w-6 h-6 sm:w-10 sm:h-10" aria-hidden="true" />
                                            </div>
                                            <div className="flex-1 text-center sm:text-left hidden sm:block">
                                                <p className="text-foreground text-sm">One of the most loved homes on Airbnb, according to guests</p>
                                            </div>
                                            <div className="flex items-center gap-4 sm:gap-8">
                                                <div className="flex flex-col items-center">
                                                    <span className="text-lg sm:text-xl font-bold text-foreground">
                                                        {apartmentReview.overallScore.toFixed(2)}
                                                    </span>
                                                    <div className="flex gap-0.5 text-foreground text-sm sm:text-base">
                                                        {[...Array(5)].map((_, i) => (
                                                            <span key={i}>★</span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="h-10 sm:h-12 w-px bg-border" />
                                                <div className="flex flex-col items-center">
                                                    <span className="text-lg sm:text-xl font-bold text-foreground">{apartmentReview.numberOfReviews}</span>
                                                    <span className="text-xs sm:text-sm text-muted-foreground">Reviews</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3">
                                            <AirbnbLogo className="h-6 text-foreground" />
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-foreground">★</span>
                                                <span className="font-semibold text-foreground">{apartmentReview.overallScore.toFixed(2)}</span>
                                                <span className="text-muted-foreground">·</span>
                                                <span className="text-foreground">{apartmentReview.numberOfReviews} reviews</span>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        {index < apartments.length - 1 && <hr className="border-border" />}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ReportGraphsView;
